import React, { useState } from 'react';
import {
  Button,
  TextField,
  Input,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  Paper,
  FormControlLabel,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import moment from 'moment';

import Dialog from '../../ui/Dialog';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay.component';
import { generateNextCycleCodeToSend, postAddCycles } from '../../../services/transaction.service';

const AddCycle = ({ data }) => {
  const searchString = data.history.location.search;
  const searchParams = new URLSearchParams(searchString);
  const patient_id = searchParams.get('patient_id');
  const program_id = data.match.params.connect_program_id;
  const [cycles, setCycles] = useState([{
    cycle: '',
    type: 'purchase',
    date: '',
    quantity: 1,
  }]);

  const [nextCode, setNextCode] = useState({
    show: false,
    nextCodeType: 'purchase',
    nextCodeCycle: '',
    nextCodeQuantity: 1,
    nextCodeQuantityPerCycle: 1,
    inCurrentTreatmentYear: true,
  });

  const [state, setState] = useState({
    openConfirmation: false,
    openAddCycleConfirmation: false,
    modalTitle: 'New Cycles',
    errorMessage: null,
    successMessage: 'Cycle has been added!',
    isLoading: false,
    submitButtonEnabled: false,
  });

  const handleClose = () => {
    const { openConfirmation } = state;
    setState(prev => ({
      ...prev,
      openConfirmation: false,
      openAddCycleConfirmation: false,
      errorMessage: null
    }));

    if (openConfirmation) {
      window.location.reload();
    }
  };

  const onAddCycle = async () => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await postAddCycles({
        program_id,
        customer_id: patient_id,
        cycles: cycles.map(cycle => ({
          cycle: cycle.cycle,
          transaction_type: cycle.type,
          quantity: parseInt(cycle.quantity.toString()),
          transaction_date: moment(cycle.date).format('DD-MM-YYYY'),
          in_current_treatment_year: nextCode.inCurrentTreatmentYear,
        })),
      });

      if (nextCode.show) {
        await generateNextCycleCodeToSend({
          program_id,
          payload: {
            customer_id: patient_id,
            next_code_type: nextCode.nextCodeType,
            cycle: nextCode.nextCodeCycle,
            quantity: nextCode.nextCodeQuantity,
            quantity_per_cycle: nextCode.nextCodeQuantityPerCycle,
          },
        });
      }
      
      setState(prev => ({
        ...prev,
        openAddCycleConfirmation: false,
        successMessage: response.message,
      }));
    } catch (e) {
      setState(prev => ({
        ...prev,
        errorMessage: e.message
      }));
    } finally {
      setState(prev => ({ ...prev, isLoading: false, openAddCycleConfirmation: false, openConfirmation: true }));
    }
  };

  const {
    openConfirmation,
    modalTitle,
    errorMessage,
    successMessage,
    isLoading,
    openAddCycleConfirmation
  } = state;

  if (isLoading) return <LoadingOverlay />;

  const handleRowChange = (index, field, value) => {
    setCycles(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const handleDuplicateRow = (index) => {
    setCycles(prev => [...prev, { ...prev[index] }]);
  };

  const handleDeleteRow = (index) => {
    setCycles(prev => prev.filter((_, i) => i !== index));
  };

  const formButtons = (
    <>
      <Button onClick={handleClose} variant="contained">
        {openConfirmation ? 'OK' : 'Close'}
      </Button>
      {
        openAddCycleConfirmation && 
        <Button
          onClick={onAddCycle}
          variant="contained"
          color="primary"
          disabled={false}
        >
          Submit
        </Button>
      }
    </>
  );

  const addCycleFormContent = (    
    <>
      <div className="completed-cycles">
        <Typography variant="caption">Completed Cycles</Typography>
        <br></br>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cycle No.*</TableCell>
                <TableCell>Type*</TableCell>
                <TableCell>Qty*</TableCell>
                <TableCell>Date*</TableCell>
                <TableCell>In Current Treatment Year?</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cycles.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      fullWidth
                      value={row.cycle}
                      onChange={(e) => handleRowChange(index, 'cycle', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      fullWidth
                      value={row.type}
                      onChange={(e) => handleRowChange(index, 'type', e.target.value)}
                    >
                      <MenuItem value="purchase">purchase</MenuItem>
                      <MenuItem value="free_good">free_good</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      fullWidth
                      value={row.quantity}
                      onChange={(e) => handleRowChange(index, 'quantity', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      fullWidth
                      value={row.date}
                      onChange={(e) => handleRowChange(index, 'date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                  <TableCell>
                    <Switch checked={nextCode.inCurrentTreatmentYear} onChange={() => setNextCode(prev => ({ ...prev, inCurrentTreatmentYear: !prev.inCurrentTreatmentYear }))} />
                  </TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => handleDuplicateRow(index)}>
                      <AddIcon />
                    </IconButton>
                    <IconButton color="primary" onClick={() => handleDeleteRow(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <div className="next-cycle-code" style={{ marginTop: '20px' }}>
        <FormControlLabel label="Generate next cycle code" 
        control={<Switch checked={nextCode.show} 
        onChange={() => setNextCode(prev => ({ ...prev, show: !prev.show }))} />} />
        { 
          nextCode.show && (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type*</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow key="next-code-type">
                  <TableCell>
                    <Select
                      fullWidth
                      value={nextCode.nextCodeType}
                      onChange={(e: any) => setNextCode(prev => ({ ...prev, nextCodeType: e.target.value }))}
                    >
                      <MenuItem value="purchase">purchase</MenuItem>
                      <MenuItem value="free_good">free_good</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          )
        }
        {
          nextCode.nextCodeType === 'free_good' && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cycle Number</TableCell>
                    <TableCell>Quantity*</TableCell>
                    <TableCell>Quantity per cycle*</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <TextField
                        fullWidth
                        value={nextCode.nextCodeCycle}
                        onChange={(e) => setNextCode(prev => ({ ...prev, nextCodeCycle: e.target.value }))}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        required
                        fullWidth
                        value={nextCode.nextCodeQuantity}
                        onChange={(e: any) => setNextCode(prev => ({ ...prev, nextCodeQuantity: e.target.value }))}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        required
                        fullWidth
                        value={nextCode.nextCodeQuantityPerCycle}
                        onChange={(e: any) => setNextCode(prev => ({ ...prev, nextCodeQuantityPerCycle: e.target.value }))}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )
        }
      </div>
    </>
  )

  return (
    <>
      <Dialog 
        key="add-cycle"
        open={openAddCycleConfirmation}
        title={modalTitle}
        content={addCycleFormContent}
        children={formButtons}
        onSubmit={onAddCycle}
        onClose={handleClose}
        titleStyle={{ marginBottom: '10px' }}
      />
      <Dialog 
        onSubmit={handleClose}
        key="confirmation"
        open={openConfirmation}
        title='New Cycle'
        content={errorMessage || successMessage}
        children={formButtons}
        onClose={handleClose}
      />
      <Button variant="contained" color="primary" onClick={() => {
         setState(prev => ({
          ...prev,
          openAddCycleConfirmation: true,
        }));
      }} style={{ marginRight: 8 }}>
        + Add
      </Button>
    </>
  );
};

export default AddCycle;